import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

import safariHeader from '../../../assets/safari/header.png';

const CASTLE_URL =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=94fbd17d-c40c-49f3-878e-d0bc415926b2';
const GARDENS_URL =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=6d614bac-7055-4932-b62b-c960e7ff213b';
const PEACOCK =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2026/04/white-peacock.png';

const S = {
  jungle: '#0C2C1B',
  forest: '#1B5E3B',
  safari: '#C8861A',
  terra: '#A83E18',
  sage: '#567A5C',
  cream: '#FBF4E8',
  parchment: '#F4EDD8',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${S.cream};
  padding-top: 158px;
`;

/* ─── MINI HERO ─────────────────────────────────── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(320px, 42vh, 520px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 38%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(12, 44, 27, 0.94) 0%,
    rgba(12, 44, 27, 0.6) 35%,
    rgba(12, 44, 27, 0.12) 68%,
    transparent 100%
  );
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.14;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(200, 134, 26, 0.35) 38px, rgba(200, 134, 26, 0.35) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(27, 94, 59, 0.5) 38px, rgba(27, 94, 59, 0.5) 40px);
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  animation: ${fadeUp} 0.65s ease both;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${S.safari};
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;

  em {
    font-style: italic;
    color: ${S.safari};
  }
`;

/* ─── MAIN SECTION ───────────────────────────────── */
const SelectionSection = styled.section`
  padding: clamp(3rem, 6vw, 5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${S.cream};
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const LeadText = styled.p`
  text-align: center;
  margin: 0 auto clamp(2.5rem, 5vw, 3.5rem);
  max-width: 540px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.1rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  line-height: 1.55;
  color: rgba(12, 44, 27, 0.7);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1rem, 2.5vw, 1.75rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

/* ─── TICKET CARD ───────────────────────────────── */
const TicketCard = styled.article`
  position: relative;
  background: #fff;
  border-radius: 18px;
  border: 1.5px solid ${p => p.$featured ? S.safari : 'rgba(28,28,30,0.09)'};
  box-shadow: ${p => p.$featured
    ? '0 8px 48px rgba(200,134,26,0.18), 0 2px 12px rgba(12,44,27,0.08)'
    : '0 4px 24px rgba(16,29,64,0.07)'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.25s ease, transform 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${p => p.$featured
      ? '0 14px 56px rgba(200,134,26,0.24), 0 4px 16px rgba(12,44,27,0.1)'
      : '0 12px 40px rgba(16,29,64,0.12)'};
  }
`;

const CardBadge = styled.div`
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  background: ${S.safari};
  color: #fff;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
`;

const CardHeader = styled.div`
  background: ${p => p.$dark ? S.jungle : S.parchment};
  padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.5rem, 3vw, 2rem);
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$dark ? 'rgba(200,134,26,0.18)' : 'rgba(200,134,26,0.12)'};
  border-radius: 12px;
  margin-bottom: 1.1rem;
`;

const CardTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => p.$light ? '#fff' : S.jungle};

  em {
    font-style: italic;
    color: ${S.safari};
  }
`;

const CardTagline = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.55;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : 'rgba(28,28,30,0.55)'};
`;

const CardBody = styled.div`
  padding: clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem);
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const IncludesList = styled.ul`
  list-style: none;
  margin: 0 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const IncludesItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.45;
  color: rgba(28, 28, 30, 0.72);

  &::before {
    content: '';
    flex-shrink: 0;
    margin-top: 0.22em;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${S.safari};
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url("data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3l2 2 4-4' stroke='white' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 8px 6px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(28, 28, 30, 0.07);
  margin: 1.25rem 0;
`;

const BookBtn = styled.a`
  display: block;
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  background: ${p => p.$primary ? S.safari : S.jungle};
  color: #fff;
  box-shadow: ${p => p.$primary
    ? '0 6px 24px rgba(200,134,26,0.38)'
    : '0 6px 24px rgba(12,44,27,0.25)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: 1.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => p.$primary
      ? '0 10px 32px rgba(200,134,26,0.48)'
      : '0 10px 32px rgba(12,44,27,0.32)'};
  }
`;

const CardNote = styled.p`
  margin: 0.75rem 0 0;
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  color: rgba(28, 28, 30, 0.38);
  letter-spacing: 0.04em;
`;

/* ─── SHARED INCLUSIONS STRIP ────────────────────── */
const SharedStrip = styled.div`
  margin-top: clamp(2rem, 4vw, 3rem);
  padding: clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 4vw, 2.5rem);
  background: ${S.jungle};
  border-radius: 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 2rem;
`;

const SharedLabel = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${S.safari};
  white-space: nowrap;
`;

const SharedItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  flex: 1;
`;

const SharedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.76rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.03em;

  &::before {
    content: '✓';
    color: ${S.safari};
    font-weight: 700;
    font-size: 0.7rem;
  }
`;

/* ─── BACK LINK ─────────────────────────────────── */
const BackLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${S.safari};
  text-transform: uppercase;
  margin-bottom: 2.5rem;
  padding: 0;
  transition: opacity 0.2s ease;

  &:hover { opacity: 0.72; }

  &::before {
    content: '←';
    font-size: 1rem;
    line-height: 1;
  }
`;

/* ─── COMPONENT ─────────────────────────────────── */
const SummerSafariTickets = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Choose Your Tickets — Belvoir Summer Safari 2026 | Belvoir Castle</title>
        <meta
          name="description"
          content="Book Belvoir Summer Safari tickets. Choose Castle entry or Gardens & Playground entry — both include the Animal Park and ice cream reward. Group Saver online. HHA and RHS members can add £4.50 Animal Park entry when booking."
        />
        <link rel="canonical" href={getCanonicalUrl('belvoir-summer-safari/tickets')} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Choose Your Tickets — Belvoir Summer Safari 2026" />
        <meta property="og:description" content="Castle or Gardens entry — Animal Park and ice cream reward included. Book online with Group Saver." />
        <meta property="og:url" content={getCanonicalUrl('belvoir-summer-safari/tickets')} />
        <meta property="og:image" content="https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/7.png" />
      </Helmet>

      <Page>
        {/* ── MINI HERO ── */}
        <HeroWrap>
          <HeroImg src={safariHeader} alt="Belvoir Summer Safari" />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>Belvoir Summer Safari · 13 July – 1 September 2026</HeroKicker>
            <HeroTitle>
              Choose Your <em>Visit</em>
            </HeroTitle>
          </HeroContent>
        </HeroWrap>

        {/* ── TICKET SELECTION ── */}
        <SelectionSection>
          <Inner>
            <BackLink onClick={() => navigate('/belvoir-summer-safari')}>
              Back to Safari
            </BackLink>

            <LeadText>
              Both tickets include the Safari Trail, Animal Park, and the free ice cream reward.
              Book 2 or more tickets together online with Group Saver — perfect for friends and family. Book early to secure your dates and avoid missing out.
            </LeadText>

            <CardGrid>
              {/* ── CASTLE TICKET ── */}
              <TicketCard $featured>
                <CardBadge>Most popular</CardBadge>
                <CardHeader $dark>
                  <CardIcon $dark>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="10" width="18" height="11" rx="1"/>
                      <path d="M3 10V7h3v3"/>
                      <path d="M10.5 10V7h3v3"/>
                      <path d="M18 10V7h3v3"/>
                      <path d="M9 21v-4a3 3 0 0 1 6 0v4"/>
                    </svg>
                  </CardIcon>
                  <CardTitle $light>
                    Castle <em>Entry</em>
                  </CardTitle>
                  <CardTagline $light>
                    The full Belvoir experience — historic State Rooms, formal gardens, playground &amp; Safari.
                  </CardTagline>
                </CardHeader>

                <CardBody>
                  <IncludesList>
                    <IncludesItem>Historic State Rooms</IncludesItem>
                    <IncludesItem>Bertie the Bear trail inside the Castle</IncludesItem>
                    <IncludesItem>Formal gardens &amp; grounds</IncludesItem>
                    <IncludesItem>Adventure Playground</IncludesItem>
                    <IncludesItem>Summer Safari Trail &amp; map</IncludesItem>
                    <IncludesItem>Animal Park — no extra ticket</IncludesItem>
                    <IncludesItem>Free ice cream reward for every child who completes the trail</IncludesItem>
                  </IncludesList>

                  <Divider />

                  <BookBtn
                    $primary
                    href={CASTLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Castle Tickets
                  </BookBtn>
                  <CardNote>Opens secure booking at belvoircastle.com</CardNote>
                </CardBody>
              </TicketCard>

              {/* ── GARDENS TICKET ── */}
              <TicketCard>
                <CardHeader>
                  <CardIcon>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a7 7 0 0 0-7 7c0 4 7 13 7 13s7-9 7-13a7 7 0 0 0-7-7z"/>
                      <circle cx="12" cy="9" r="2.5"/>
                    </svg>
                  </CardIcon>
                  <CardTitle>
                    Gardens &amp; <em>Playground</em>
                  </CardTitle>
                  <CardTagline>
                    A full outdoor day — gardens, adventure playground, and the Safari Trail.
                  </CardTagline>
                </CardHeader>

                <CardBody>
                  <IncludesList>
                    <IncludesItem>Formal gardens &amp; grounds</IncludesItem>
                    <IncludesItem>Adventure Playground</IncludesItem>
                    <IncludesItem>Summer Safari Trail &amp; map</IncludesItem>
                    <IncludesItem>Animal Park — no extra ticket</IncludesItem>
                    <IncludesItem>Free ice cream reward for every child who completes the trail</IncludesItem>
                  </IncludesList>

                  <Divider />

                  <BookBtn
                    href={GARDENS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Gardens Tickets
                  </BookBtn>
                  <CardNote>Opens secure booking at belvoircastle.com</CardNote>
                </CardBody>
              </TicketCard>
            </CardGrid>

            {/* ── SHARED INCLUSIONS ── */}
            <SharedStrip>
              <SharedLabel>Both tickets include</SharedLabel>
              <SharedItems>
                <SharedPill>Summer Safari Trail</SharedPill>
                <SharedPill>Animal Park</SharedPill>
                <SharedPill>Free Ice Cream Reward</SharedPill>
                <SharedPill>Adventure Playground</SharedPill>
                <SharedPill>Group Saver available</SharedPill>
              </SharedItems>
              <img
                src={PEACOCK}
                alt=""
                style={{ height: 38, width: 'auto', opacity: 0.55, flexShrink: 0 }}
              />
            </SharedStrip>

            <p style={{
              margin: 'clamp(1.5rem, 3vw, 2rem) auto 0',
              maxWidth: '40rem',
              padding: '1rem 1.15rem',
              borderRadius: 10,
              border: '1px solid rgba(200,134,26,0.28)',
              background: 'rgba(200,134,26,0.08)',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '0.88rem',
              lineHeight: 1.55,
              color: 'rgba(28,28,30,0.72)',
              textAlign: 'center',
            }}>
              <strong style={{ color: S.jungle }}>Please note:</strong> HHA Members and RHS Members are required to pay £4.50 to enter the Animal Park. This can be added when booking online.
            </p>

            <p style={{
              textAlign: 'center',
              marginTop: '1.5rem',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '0.76rem',
              color: 'rgba(28,28,30,0.38)',
              letterSpacing: '0.04em',
            }}>
              Trail open 13 July – 1 September 2026 · Online booking only for best price · Mon–Sun 09:30–17:00
            </p>
          </Inner>
        </SelectionSection>

        <Footer />
      </Page>
    </>
  );
};

export default SummerSafariTickets;
